import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorMessage, FieldArray, Formik } from 'formik';
import { toast } from 'react-toastify';
import {
  useAddFloristInventoryMutation,
  useAddProductMutation,
  useGetSingleFloristProductQuery,
  useGetSingleProductQuery,
} from './productsApiSlice';
import { useNavigate, useParams } from 'react-router-dom';
import {
  productDefaultImage,
  productDefaultValues,
  productSchema,
  weightTypeList,
} from './assets/productsUtil';
import { Col, Form, Row, OverlayTrigger, Collapse } from 'react-bootstrap';
import styled from 'styled-components';
import CheckIcon from '../../app/components/checkbox/CheckIcon';
import { getNameFromURL, isAuthorized } from '../../utils/helper';
import DeleteIcon from '../../app/components/svgIcons/DeleteIcon';
import { ShopStyled } from '../shopSetting/assets/shopStyled';
import { formErrorMessage } from '../../app/components/formikComponents/FormErrorMessage';
import NewArrowLeftIcon from '../../app/components/svgIcons/NewArrowLeftIcon';
import StyledText from '../../app/components/styledComponents/StyledText';
import InfoIcon from '../../app/components/svgIcons/InfoIcon';
import { StyledTooltip } from '../../app/components/styledComponents/StyledTooltip';
import AddIcon from '../../app/components/svgIcons/AddIcon';
import HelpIcon from '../../app/components/svgIcons/HelpIcon';
import {
  useGetAllNewCategoriesMutation,
  useGetProductCategoriesMutation,
} from '../vendors/vendorsApiSlice';
import DropDownIcon from '../../app/components/svgIcons/DropDownIcon';
import StyledTopTag from '../../app/components/styledComponents/StyledTopTag';
import StyledLoading from '../../app/components/loading/StyledLoading';
import AddOnsListView from '../addons/addonSelect';
import { useGetAddonsQuery } from '../addons/addonApiSlice';
import CircleCrossIcon from '../../app/components/svgIcons/CircleCrossIcon';
import DeleteConfirmation from '../../app/components/popups/DeleteConfirmation';
import StyledCheckbox from '../../app/components/checkbox/StyledCheckbox';
import { Container } from 'reactstrap';

//Formik Inputs
import FormikHTMLInputField from '../../app/components/formikComponents/FormikHTMLInputField';
import FormikDropZone from '../../app/components/formikComponents/FormikDropZone';
import FormikSelect from '../../app/components/formikComponents/FormikSelect';
import FormikInputWithIcon from '../../app/components/formikComponents/FormikInputWithIcon';
import FormikRadio from '../../app/components/formikComponents/FormikRadio';
import FormikNewTagsSearch from '../../app/components/formikComponents/FormikNewTagsSearch';
import FormikDropDown from '../../app/components/formikComponents/FormikDropDown';
import StyledSwitch from '../../app/components/styledComponents/StyledSwitch';
import { useDispatch } from 'react-redux';
import { openHelpSection } from '../../app/services/helpSectionSlice';
import { HELPSECTIONTOPICS } from '../../utils/Constants';
import RoundedCloseIcon from '../../app/components/svgIcons/RoundedCloseIcon';
import StyledButton from '../../app/components/styledComponents/StyledButton';
import { CarouselTheming } from '../settings/EditAndPreviewTemplate/components/PagesView/Hero/styles';
import EditIcon from '../../app/components/svgIcons/EditIcon';
import ImageSelectionModal from '../settings/components/ImageSelectionModal';
import {
  setProductsState,
  setRefetchedAfterUpdate,
} from '../../app/services/products';
import useAuth from '../../hooks/useAuth';
import { useSelector } from 'react-redux';
import SortableList, { SortableItem } from 'react-easy-sort';
import DragIcon from '../settings/EditAndPreviewTemplate/assets/dragIcon';
import FormikDatePicker from '../../app/components/formikComponents/FormikDatePicker';
import moment from 'moment';
import { useGetRegionalSettingsQuery } from '../shopSetting/shopAPISlice';

const CreateProduct = ({
  isFlorist,
  isCatalogue,
  isViewOnly,
  isComingFromEditTemplate,
  onHandleButtonActionForTemplate,
  productId,
}) => {
  const { user } = useAuth();
  const ProductsNavRoute = isFlorist ? '/settings/products' : '/products';
  const arrayHelperRef = useRef();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [pid] = useState(id || productId);
  const addMutation = isFlorist
    ? useAddFloristInventoryMutation
    : useAddProductMutation;

  const getSingle = isFlorist
    ? useGetSingleFloristProductQuery
    : useGetSingleProductQuery;

  const [addProductAPI, { isLoading: loading }] = addMutation();
  const {
    data: singleProduct,
    isLoading: isProductLoading,
    isFetching: isFetchingProduct,
  } = getSingle(pid, {
    skip: !pid,
    refetchOnMountOrArgChange: true,
  });

  const [getAllNewProductCategories] = useGetAllNewCategoriesMutation();
  const { data: addonList } = useGetAddonsQuery();

  const [categories, setCategories] = useState([]);
  const [showDeletePopUp, setShowDeletePopUp] = useState(false);
  const [deleteIndexId, setDeleteIndexId] = useState(-1);
  const [openImageGalleryModal, setOpenImageGalleryModal] = useState(null);
  const [isButtonDisable, setIsButtonDisable] = useState(false);
  const formikRef = useRef(null);

  const userPermissions = useSelector(
    (state) => state?.authorization?.userPermissions,
  );

  const has_permission_to_add_product = isAuthorized(
    userPermissions,
    'add_product',
  );

   const { data: regionalSettings } = useGetRegionalSettingsQuery();

   const timeZoneString =
   regionalSettings && regionalSettings?.timezone
     ? regionalSettings?.timezone?.match(/\(([^)]+)\)$/)?.[1]
     : '';

  const has_permission_to_edit = isAuthorized(userPermissions, 'edit_product');

  async function loadOptions() {
    let param = {
      size: 5,
      page: 0,
    };
    try {
      const { data } = await getAllNewProductCategories(param);
      const options = await data?.options?.map((option) => ({
        ...option,
        value: option.id,
        label: option.title,
      }));
      setCategories(options);
    } catch (e) {
      return setCategories([]);
    }
  }

  const navigate = useNavigate();
  const [productImages, setProductImages] = useState([]);

  useEffect(() => {
    if (
      singleProduct?.objectData?.inventoryImages ||
      singleProduct?.objectData?.floristInventoryImages
    ) {
      setProductImages(
        singleProduct?.objectData?.inventoryImages ||
          singleProduct?.objectData?.floristInventoryImages,
      );
    }
  }, [
    singleProduct?.objectData?.inventoryImages ||
      singleProduct?.objectData?.floristInventoryImages,
  ]);

  useEffect(() => {
    loadOptions();
    getProductTags();
  }, []);

  const onDropBulkUpload = useCallback((acceptedFiles) => {
    acceptedFiles.forEach(async (file) => {
      const temporaryImageUrl = URL.createObjectURL(file);
      setProductImages((prevState) => [
        ...prevState,
        prevState.length < 1
          ? {
              fileName: file?.name || '',
              extension: `.${file?.name?.split('.')?.pop() || ''}`,
              thumbnail: true,
              url: temporaryImageUrl,
              file,
            }
          : {
              fileName: file?.name || '',
              url: temporaryImageUrl,
              extension: `.${file?.name?.split('.')?.pop() || ''}`,
              thumbnail: false,
              file,
            },
      ]);
    });
  }, []);

  const onDropBulkUploadVariations = useCallback(
    async (prevFiles, acceptedFiles) => {
      const result = await Promise.all(
        acceptedFiles.map(async (file, index) => {
          const temporaryImageUrl = URL.createObjectURL(file);
          return {
            url: temporaryImageUrl,
            fileName: file?.name || '',
            extension: `.${file?.name?.split('.')?.pop() || ''}`,
            thumbnail: prevFiles.length < 1 && index === 0,
            file,
          };
        }),
      );

      return [...prevFiles, ...result];
    },
    [],
  );

  const [getProductCategories] = useGetProductCategoriesMutation();
  const [productTags, setProductTags] = useState([]);

  const getProductTags = async () => {
    try {
      const { data } = await getProductCategories({
        page: 0,
        size: 1000,
      });

      const options = await data?.options?.map((option) => ({
        ...option,
        value: option.title,
        label: option.title,
      }));

      setProductTags(options);
    } catch (error) {}
  };

  const variationKey = isFlorist ? 'floristVariationDTOS' : 'variationDTOS';

  const handleAddProduct = async (values) => {
    if (
      !isCatalogue &&
      (pid ? !has_permission_to_edit : !has_permission_to_add_product)
    ) {
      return;
    }
    if (isFlorist) {
      values.floristInventoryImages = productImages ?? [];
      values.floristInventoryTagDTOS = values.inventoryTagDTOS || [];
    } else {
      values.inventoryImages = productImages ?? [];
    }

    if (values.categoryDTO) {
      values.categoryDTO = categories.find((c) => c.id === values.categoryDTO);
    }

    values.isFlorist = isFlorist;
    delete values.images;

    try {
     addProductAPI({
       values: {
         ...values,
         floristId: user?.floristId ?? user?.id ?? user?.floristDTO?.id,
       },
       uuid: user?.floristDTO?.uuid,
     });
      dispatch(
        setProductsState({
          data: { ...values, uploading: true, progress: 0 },
          isFlorist,
        }),
      );
      dispatch(
        setRefetchedAfterUpdate({
          refetchedAfterUpdate: false,
        }),
      );
      if (isComingFromEditTemplate) {
        onHandleButtonActionForTemplate();
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const toggleSection = (id) => {
    setProductTags((prevSections) =>
      prevSections.map((section) =>
        section.id === id ? { ...section, open: !section.open } : section,
      ),
    );
  };

  const isSelectedTag = (id, selectedTags) =>
    selectedTags.find((tag) => tag.id === id);

  const handleSelectThumbnail = (ind) => {
    setProductImages((prevState) => [
      ...prevState.map((p, index) => {
        return ind === index
          ? {
              ...p,
              thumbnail: true,
            }
          : {
              ...p,
              thumbnail: false,
            };
      }),
    ]);
  };

  const handleDeleteClick = async () => {
    if (deleteIndexId !== -1) {
      if (
        arrayHelperRef.current.form.values?.[variationKey]?.[deleteIndexId]
          ?.defaultVariation
      ) {
        if (
          arrayHelperRef?.current?.form?.values?.[variationKey]?.length - 1 >
          deleteIndexId
        ) {
          const nextItem =
            arrayHelperRef.current.form.values?.[variationKey]?.[
              deleteIndexId + 1
            ];
          arrayHelperRef.current.replace(deleteIndexId + 1, {
            ...nextItem,
            defaultVariation: true,
          });
        } else if (deleteIndexId !== 0) {
          const nextItem =
            arrayHelperRef.current.form.values?.[variationKey]?.[
              deleteIndexId - 1
            ];
          arrayHelperRef.current.replace(deleteIndexId - 1, {
            ...nextItem,
            defaultVariation: true,
          });
        }
      }
      arrayHelperRef.current.remove(deleteIndexId);
      setDeleteIndexId(-1);
    }
  };

  const handleOpenHelpSection = (title) => {
    dispatch(
      openHelpSection({
        show: true,
        comingFrom: title,
      }),
    );
  };

  const setSelectedImage = (imgUrl) => {
    if (openImageGalleryModal?.isProduct) {
      setProductImages((prevState) => [
        ...prevState,
        createImageDTOObject(imgUrl, prevState.length < 1, true),
      ]);
    }

    if (openImageGalleryModal?.isProductVariation) {
      const variationImages =
        formikRef?.current?.values?.[variationKey]?.[
          openImageGalleryModal?.variationIndex
        ]?.imageDTOList ?? [];

      formikRef?.current?.setFieldValue(
        `${variationKey}.${openImageGalleryModal?.variationIndex}.imageDTOList`,
        [
          ...variationImages,
          createImageDTOObject(imgUrl, variationImages?.length < 1, true),
        ],
      );
    }
    setOpenImageGalleryModal(null);
  };

  const createImageDTOObject = (
    imgUrl,
    isFirst = false,
    isGallerySelected = false,
  ) => {
    return {
      content: null,
      displayPicture: false,
      extension: null,
      fileName: getNameFromURL(imgUrl) || '',
      thumbnail: isFirst,
      thumbnailUrl: imgUrl
        ? imgUrl.replace('//images.', '//thumbnails.')
        : null,
      url: imgUrl,
      gallerySelected: isGallerySelected,
    };
  };

  const getAddOnPrice = (addons) => {
    if (addons && addons?.price) {
      return addons?.price || 0;
    }

    if (
      addons &&
      addons?.variationDTOList &&
      addons?.variationDTOList?.length > 0
    ) {
      const prices = addons.variationDTOList
        .map((item) => item?.price)
        .filter((price) => price != null);

      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        // Return the price range if min and max are different
        return minPrice === maxPrice
          ? `${minPrice}`
          : `${minPrice} - ${maxPrice}`;
      }
    }

    return 0;
  };

  const renderInput = ({
    label,
    toolTip,
    component,
    isRequired,
    parentClass = '',
    hideToolTip = false,
    onToolTipClick,
    horizontal = false,
    lableClassName = '',
  }) => (
    <div
      className={`mb-3 ${parentClass ?? ''} ${
        horizontal ? 'd-flex flex-row align-items-center' : ''
      }`}
    >
      <ShopStyled.NewFormLabel
        style={{ marginRight: horizontal ? '31px' : '' }}
      >
        {hideToolTip ? (
          <span
            className={`title ${isRequired ? 'required' : ''} ${
              lableClassName ? lableClassName : ''
            }`}
          >
            {label}
          </span>
        ) : (
          <>
            <span
              className={`title ${isRequired ? 'required' : ''} ${
                lableClassName ? lableClassName : ''
              }`}
            >
              {label}
            </span>
            <OverlayTrigger
              overlay={<StyledTooltip>{toolTip || label}</StyledTooltip>}
            >
              <span className="cursor-pointer ms-3 ">
                {onToolTipClick ? (
                  <HelpIcon
                    className="mb-1"
                    {...(onToolTipClick && { onClick: onToolTipClick })}
                  />
                ) : (
                  <InfoIcon className="mb-1" />
                )}
              </span>
            </OverlayTrigger>
          </>
        )}
      </ShopStyled.NewFormLabel>

      {component}
    </div>
  );

  const scrollToFirstError = (errors) => {
    if (errors) {
      for (const key in errors) {
        if (errors[key]) {
          const element = document.querySelector(`[id="${key}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
            break;
          }
        }
      }
    }
    if (errors?.floristVariationDTOS && Object.keys(errors).length == 1) {
      let found = false;
      errors?.floristVariationDTOS.forEach((errors, index) => {
        if (found) return;
        for (const key in errors) {
          if (errors[key]) {
            const element = document.querySelector(
              `[id="floristVariationDTOS.${index}.${key}"]`,
            );
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.focus();
              found = true;
              break;
            }
          }
        }
      });
    }
  };

  return (
    <>
      <ShopStyled.PageWrap isFullWidth>
        <Container fluid>
          {!isProductLoading && !isFetchingProduct ? (
            <Formik
              innerRef={formikRef}
              enableReinitialize
              initialValues={{
                ...productDefaultValues,
                inventoryTagDTOS: [],
                ...(singleProduct?.objectData ? singleProduct?.objectData : {}),
                ...(pid
                  ? {
                      categoryDTO: singleProduct?.objectData?.categoryDTO?.id,
                      stockStatus: !singleProduct?.objectData?.stockStatus
                        ? false
                        : true,
                      inventoryTagDTOS: isFlorist
                        ? singleProduct?.objectData?.floristInventoryTagDTOS?.map(
                            (o) => ({
                              ...o,
                              label: o.name,
                              value: o.id,
                            }),
                          ) || []
                        : singleProduct?.objectData?.inventoryTagDTOS?.map(
                            (o) => ({
                              ...o,
                              label: o.name,
                              value: o.id,
                            }),
                          ) || [],
                    }
                  : {
                      categoryDTO: 1,
                      stockStatus: true,
                    }),
                availableFromDate:
                  singleProduct?.objectData?.availableDateStatus &&
                  singleProduct?.objectData?.availableFromDate
                    ? singleProduct?.objectData?.availableFromDate
                    : moment(new Date()).format('YYYY-MM-DD'),

                availableToDate:
                  singleProduct?.objectData?.availableDateStatus &&
                  singleProduct?.objectData?.availableToDate
                    ? singleProduct?.objectData?.availableToDate
                    : moment(new Date()).format('YYYY-MM-DD'),
              }}
              validationSchema={productSchema(variationKey)}
              validateOnChange
              onSubmit={handleAddProduct}
            >
              {({
                values,
                setFieldValue,
                handleSubmit,
                submitCount,
                errors,
                validateForm,
              }) => {
                return (
                  <Form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const formErrors = await validateForm();
                      scrollToFirstError(formErrors);
                      handleSubmit();
                    }}
                  >
                    <ShopStyled.BootstrapCard className="p-0">
                      <ShopStyled.NewPageTitle hasBorder>
                        <div className="d-flex align-items-center">
                          <div className="heading heading24 ms-1 ms-md-2 d-flex align-items-center gap-2 gap-md-3">
                            <span
                              className="back-icon"
                              onClick={() =>
                                isComingFromEditTemplate
                                  ? onHandleButtonActionForTemplate()
                                  : navigate(ProductsNavRoute)
                              }
                            >
                              <NewArrowLeftIcon />
                            </span>

                            {!isViewOnly ? (
                              <span>{`${pid ? 'Edit' : 'Add'} Product`}</span>
                            ) : (
                              <span>Product Details</span>
                            )}
                          </div>
                          -
                        </div>
                      </ShopStyled.NewPageTitle>
                      <ShopStyled.BootstrapCard className="m-4">
                        <Row className="mt-2 mt-md-3">
                          <Col xs={12} md={6}>
                            {renderInput({
                              label: 'Product Title',
                              toolTip: 'Product Title',
                              isRequired: true,
                              component: (
                                <FormikInputWithIcon
                                  name={`itemName`}
                                  id={`itemName`}
                                  placeholder="Enter Title"
                                />
                              ),
                            })}

                            {renderInput({
                              label: 'Notes',
                              toolTip: 'Notes',
                              component: (
                                <FormikInputWithIcon
                                  name={`notes`}
                                  id={`notes`}
                                  placeholder="Enter Notes"
                                />
                              ),
                            })}

                            {renderInput({
                              label: 'Description',
                              toolTip: 'Description',
                              isRequired: true,
                              component: (
                                <div
                                  id="description"
                                  style={{ color: 'black' }}
                                  name="description"
                                >
                                  <FormikHTMLInputField
                                    onCharacterCountExceed={setIsButtonDisable}
                                    as="textarea"
                                    name={`description`}
                                    id={`description`}
                                    placeholder="Enter Description"
                                  />
                                </div>
                              ),
                            })}

                            <Row>
                              <Col xs={12}>
                                <Row>
                                  <Col xs={12} md={6}>
                                    {renderInput({
                                      label: 'Deactivate Product',
                                      toolTip: 'Deactivate Product',
                                      onToolTipClick: () =>
                                        handleOpenHelpSection(
                                          HELPSECTIONTOPICS.DEACTIVATEPRODUCT,
                                        ),
                                      component: (
                                        <div className="d-flex align-items-center gap-1 mb-2 DeactivateProduct">
                                          <StyledSwitch
                                            enableText="Inactive"
                                            disableText="Active"
                                            width="100px"
                                            checked={!values?.activated}
                                            onChange={(e) =>
                                              setFieldValue('activated', !e)
                                            }
                                          />
                                        </div>
                                      ),
                                    })}
                                  </Col>

                                  <Col xs={12} md={6}>
                                    {renderInput({
                                      label: 'Local Delivery Only',
                                      toolTip: 'Local Delivery Only',
                                      onToolTipClick: () =>
                                        handleOpenHelpSection(
                                          HELPSECTIONTOPICS.LOCALDELIVERYONLY,
                                        ),
                                      component: (
                                        <div className="d-flex align-items-center gap-1 mb-2 LocalDeliveryOnly">
                                          <StyledSwitch
                                            enableText="Local"
                                            disableText="Global"
                                            width="100px"
                                            checked={values?.localDeliveryOnly}
                                            onChange={(e) =>
                                              setFieldValue(
                                                'localDeliveryOnly',
                                                e,
                                              )
                                            }
                                          />
                                        </div>
                                      ),
                                    })}
                                  </Col>
                                </Row>
                              </Col>

                              <Col xs={12}>
                                <Row>
                                  <Col xs={12} md={6}>
                                    {renderInput({
                                      label: 'Hide from product page',
                                      toolTip: 'Hide from product page',
                                      onToolTipClick: () =>
                                        handleOpenHelpSection(
                                          HELPSECTIONTOPICS.HIDEFROMPRODUCTPAGE,
                                        ),
                                      component: (
                                        <div className="d-flex gap-1 align-items-center Hidefromproductpage">
                                          <StyledSwitch
                                            enableText="Hidden"
                                            disableText="Visible"
                                            width="100px"
                                            checked={values?.hidden}
                                            onChange={(e) =>
                                              setFieldValue('hidden', e)
                                            }
                                          />
                                        </div>
                                      ),
                                    })}
                                  </Col>

                                  {isFlorist && (
                                    <Col xs={12} md={6}>
                                      {renderInput({
                                        label: 'Stock Status',
                                        toolTip: 'Stock Status',
                                        component: (
                                          <OverlayTrigger
                                            overlay={
                                              <StyledTooltip>
                                                If you mark the product as out
                                                of stock here, all variations of
                                                the product will be out of
                                                stock.
                                              </StyledTooltip>
                                            }
                                          >
                                            <div className="d-flex align-items-center gap-1 mb-2">
                                              <StyledSwitch
                                                enableText="In Stock"
                                                disableText="Out of Stock"
                                                width="120px"
                                                checked={values?.stockStatus}
                                                onChange={(e) => {
                                                  setFieldValue(
                                                    `stockStatus`,
                                                    e,
                                                  );
                                                }}
                                              />
                                            </div>
                                          </OverlayTrigger>
                                        ),
                                      })}
                                    </Col>
                                  )}
                                </Row>
                              </Col>
                            </Row>
                          </Col>

                          <Col xs={12} md={6}>
                            {renderInput({
                              label: 'Product Images',
                              toolTip: 'Product Images',
                              component: (
                                <div name="productImages">
                                  <FormikDropZone
                                    size="small"
                                    name="images"
                                    id="images"
                                    onDrop={(val) => {
                                      setFieldValue('images', val);
                                      onDropBulkUpload(val);
                                    }}
                                  />
                                  <CarouselTheming.SelectImageButton
                                    className="mt-2"
                                    onClick={() => {
                                      setOpenImageGalleryModal({
                                        isProduct: true,
                                      });
                                    }}
                                    bgprimary="true"
                                  >
                                    <EditIcon /> Select Image From Gallery
                                  </CarouselTheming.SelectImageButton>
                                </div>
                              ),
                            })}

                            <div className="mb-2">
                              <div className="d-flex gap-2 flex-wrap">
                                {productImages?.map((img, ind) => (
                                  <StyledImageContainer key={ind + '-pro-img'}>
                                    <StyledUserImage
                                      onClick={() => handleSelectThumbnail(ind)}
                                      className={`cursor-pointer flex-center align-items-center justify-content-center ${
                                        img?.thumbnail ? 'active' : ''
                                      }`}
                                    >
                                      <img
                                        loading="lazy"
                                        src={img?.thumbnailUrl || img?.url}
                                        alt={img?.fileName}
                                      />
                                      <div className="check-icon">
                                        <CheckIcon />
                                      </div>
                                    </StyledUserImage>
                                    <div
                                      className="delete-icon cursor-pointer"
                                      onClick={() =>
                                        setProductImages((prevState) => {
                                          // Filter the images based on id or index
                                          let filteredImages = prevState.filter(
                                            (p, index) =>
                                              img.id
                                                ? p.id !== img.id
                                                : index !== ind,
                                          );

                                          // Update the thumbnail property
                                          if (
                                            filteredImages &&
                                            filteredImages.length > 0
                                          ) {
                                            filteredImages = filteredImages.map(
                                              (image, i) => ({
                                                ...image,
                                                thumbnail: i === 0, // Set thumbnail to true for the first image, false for the others
                                              }),
                                            );
                                          }

                                          return filteredImages;
                                        })
                                      }
                                    >
                                      <DeleteIcon />
                                    </div>
                                  </StyledImageContainer>
                                ))}
                              </div>
                            </div>
                            <StyledText $fontSize="14px" color="bodyMedium">
                              PNG/JPG/JPEG are supported. Max file size is 2MB
                            </StyledText>

                            <div className="mt-4">
                              {renderInput({
                                label: 'Recipe (Internal Only)',
                                toolTip: 'Recipe (Internal Only)',
                                isRequired: false,
                                component: (
                                  <div style={{ color: 'black' }} name="recipe">
                                    <FormikHTMLInputField
                                      as="textarea"
                                      onCharacterCountExceed={
                                        setIsButtonDisable
                                      }
                                      name={`recipe`}
                                      id={`recipe`}
                                      placeholder="Enter Recipe"
                                    />
                                  </div>
                                ),
                              })}
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={12} md={3}>
                            <>
                              {renderInput({
                                label: 'Set Availability Date',
                                toolTip: 'Set Availability Date',
                                isRequired: false,
                                parentClass: 'mt-0',
                                component: (
                                  <StyledSwitch
                                    name={`availableDateStatus`}
                                    checked={values?.availableDateStatus}
                                    onChange={(e) =>
                                      setFieldValue(`availableDateStatus`, e)
                                    }
                                  />
                                ),
                              })}
                            </>
                          </Col>
                          <Col xs={12} md={9}>
                            {values?.availableDateStatus && (
                              <Row>
                                <Col xs={12} md={3}>
                                  {renderInput({
                                    label: 'From date',
                                    toolTip: 'From date',
                                    parentClass: 'w-100',
                                    component: (
                                      <FormikDatePicker
                                        disableYear={true}
                                        disableDate={true}
                                        name={'availableFromDate'}
                                        placeholder="Enter Start Date"
                                        onChange={(name, val) => {
                                          setFieldValue(
                                            'availableFromDate',
                                            moment(val).format('YYYY-MM-DD'),
                                          );
                                        }}
                                      />
                                    ),
                                  })}
                                </Col>
                                <Col xs={12} md={3}>
                                  {renderInput({
                                    label: 'To date',
                                    toolTip: 'To date',
                                    parentClass: 'w-100',
                                    component: (
                                      <FormikDatePicker
                                        disableYear={true}
                                        disableDate={true}
                                        name={'availableToDate'}
                                        placeholder="Enter end Date"
                                        onChange={(name, val) => {
                                          setFieldValue(
                                            'availableToDate',
                                            moment(val).format('YYYY-MM-DD'),
                                          );
                                        }}
                                      />
                                    ),
                                  })}
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                      </ShopStyled.BootstrapCard>

                      {!isCatalogue && (
                        <>
                          {addonList?.content?.length ||
                          values?.addonsDTOS?.length ? (
                            <ShopStyled.BootstrapCard className="m-4">
                              <StyledText
                                as="h5"
                                $fontSize="24px"
                                color="heading"
                                $fontWeight="600"
                              >
                                Add-ons
                              </StyledText>

                              <SortableList
                                onSortEnd={(oldIndex, newIndex) => {
                                  let newItems = [...values?.addonsDTOS];
                                  const [removed] = newItems.splice(
                                    oldIndex,
                                    1,
                                  );
                                  newItems.splice(newIndex, 0, removed);
                                  setFieldValue(`addonsDTOS`, newItems);
                                }}
                                draggedItemClassName="dragged"
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  flexWrap: 'wrap',
                                  overflow: 'visible',
                                }}
                              >
                                {values?.addonsDTOS?.map((addon, index) => (
                                  <SortableItem key={index}>
                                    <AddonBg>
                                      <DragIcon height={30} width={30} />
                                      <img
                                        alt=""
                                        loading="lazy"
                                        src={
                                          addon?.addonsDto?.imageUrl ??
                                          productDefaultImage
                                        }
                                        style={{ maxHeight: '50px' }}
                                      />
                                      <div>
                                        <StyledText
                                          as="h5"
                                          $fontSize="20px"
                                          color="heading"
                                          $fontWeight="500"
                                        >
                                          {addon?.addonsDto?.title}
                                        </StyledText>
                                        <StyledText
                                          as="h5"
                                          $fontSize="18px"
                                          color="heading"
                                          $fontWeight="400"
                                        >
                                          ${getAddOnPrice(addon?.addonsDto)}
                                        </StyledText>
                                      </div>
                                      <DeleteIconBg
                                        onClick={() => {
                                          setFieldValue(
                                            'addonsDTOS',
                                            values.addonsDTOS.filter(
                                              (obj) =>
                                                obj?.addonsDto?.id !==
                                                addon?.addonsDto?.id,
                                            ),
                                          );
                                        }}
                                      >
                                        <CircleCrossIcon size={24} />
                                      </DeleteIconBg>
                                    </AddonBg>
                                  </SortableItem>
                                ))}
                              </SortableList>

                              {addonList?.content?.length ? (
                                <AddOnsListView
                                  addonList={addonList?.content}
                                  selectedList={values.addonsDTOS}
                                  getAddOnPrice={getAddOnPrice}
                                  onChangeAddon={(e) => {
                                    const object = {
                                      addonId: e?.id,
                                      addonsDto: e,
                                    };
                                    const ary = values.addonsDTOS
                                      ? [...values.addonsDTOS, object]
                                      : [object];
                                    setFieldValue('addonsDTOS', ary);
                                  }}
                                />
                              ) : (
                                <></>
                              )}
                            </ShopStyled.BootstrapCard>
                          ) : (
                            <></>
                          )}
                        </>
                      )}

                      <ShopStyled.BootstrapCard className="m-4">
                        <StyledText
                          as="h5"
                          $fontSize="24px"
                          color="heading"
                          $fontWeight="600"
                        >
                          Category & Tags
                        </StyledText>
                        {categories?.length > 0 && (
                          <div className="mt-2">
                            {renderInput({
                              label: 'Category',
                              isRequired: true,
                              hideToolTip: true,
                              component: (
                                <FormikRadio
                                  name="categoryDTO"
                                  className="justify-content-start gap-3 gap-md-5 pt-lg-0"
                                  radioLabelClassName="fw-normal fs-5"
                                  options={categories || []}
                                />
                              ),
                            })}
                          </div>
                        )}
                        {renderInput({
                          label: 'Tags',
                          toolTip: 'Tags',
                          isRequired: true,
                          onToolTipClick: () =>
                            handleOpenHelpSection(
                              HELPSECTIONTOPICS.ADDPRODUCTTAGS,
                            ),
                          component: (
                            <div id="inventoryTagDTOS">
                              <FormikNewTagsSearch
                                name="inventoryTagDTOSList"
                                placeholder="Select Tags"
                                options={productTags}
                                setOptions={setProductTags}
                                previousData={productDefaultValues}
                                secondName={'inventoryTagDTOS'}
                              />
                            </div>
                          ),
                        })}
                        <hr className="mb-5" />

                        {productTags &&
                          productTags
                            ?.filter(
                              (option) =>
                                option.parentCategory === 0 &&
                                option.childTags.length > 0,
                            )
                            ?.map((option) => ({
                              ...option,
                              childTags: option.childTags.map((childTag) => ({
                                ...childTag,
                                value: childTag.name,
                                label: childTag.name,
                              })),
                            }))
                            ?.sort((a, b) => {
                              if (a.name === 'Top Tags') {
                                return -1;
                              }
                              if (b.name === 'Top Tags') {
                                return 1;
                              }
                              return 0;
                            })
                            ?.map((section, index) => (
                              <div key={`top-tags-${index}-${section.id}`}>
                                <ShopStyled.BootstrapCollapseCard>
                                  <div
                                    className="d-flex justify-content-between align-items-center cursor-pointer"
                                    onClick={() => toggleSection(section.id)}
                                    aria-controls={`section-${section.id}`}
                                    aria-expanded={section.open}
                                  >
                                    <StyledText
                                      as="h5"
                                      $fontSize="20px"
                                      color="heading"
                                      $fontWeight="600"
                                    >
                                      {section?.name || 'heading'}
                                    </StyledText>
                                    <StyledText color="icon" className="mb-0 ">
                                      <DropDownIcon
                                        rotate={`${section.open ? 'up' : ''}`}
                                      />
                                    </StyledText>
                                  </div>
                                  <Collapse in={section.open}>
                                    <div id={`section-${section.id}`}>
                                      <div className="d-flex flex-wrap gap-3 my-2 my-md-3">
                                        {section.childTags.map(
                                          (checkbox, i) => (
                                            <StyledTopTag
                                              key={`${checkbox.id}-${i}`}
                                              className={`${
                                                isSelectedTag(
                                                  checkbox.id,
                                                  values.inventoryTagDTOS,
                                                )
                                                  ? 'active'
                                                  : ''
                                              }`}
                                              onClick={() => {
                                                if (
                                                  isSelectedTag(
                                                    checkbox.id,
                                                    values.inventoryTagDTOS,
                                                  )
                                                ) {
                                                  setFieldValue(
                                                    'inventoryTagDTOS',
                                                    values.inventoryTagDTOS.filter(
                                                      (tag) =>
                                                        tag.id !== checkbox.id,
                                                    ),
                                                  );
                                                } else {
                                                  setFieldValue(
                                                    'inventoryTagDTOS',
                                                    [
                                                      ...values.inventoryTagDTOS,
                                                      checkbox,
                                                    ],
                                                  );
                                                }
                                              }}
                                            >
                                              {checkbox.name || 'tag'}
                                              <AddIcon size="10px" />
                                            </StyledTopTag>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  </Collapse>
                                </ShopStyled.BootstrapCollapseCard>
                              </div>
                            ))}
                      </ShopStyled.BootstrapCard>
                      <ShopStyled.BootstrapCard className="m-4 mt-0">
                        <StyledText
                          as="h5"
                          $fontSize="24px"
                          color="heading"
                          $fontWeight="600"
                        >
                          Details
                        </StyledText>

                        <Row className="mt-2 mt-md-3">
                          <Col xs={12} md={6}>
                            {renderInput({
                              label: !isCatalogue ? 'Price' : 'Suggested Price',
                              toolTip: !isCatalogue
                                ? 'Price'
                                : 'Suggested Price',
                              component: (
                                <>
                                  <InputFieldContainer
                                    disabled={
                                      values[variationKey]?.length
                                        ? true
                                        : false
                                    }
                                  >
                                    <div className="d-flex align-items-start flex-column flex-grow-1">
                                      <FormikInputWithIcon
                                        name={`price`}
                                        id={`price`}
                                        icon={<>$</>}
                                        formatType="DECIMAL"
                                        disabled={
                                          values[variationKey]?.length
                                            ? true
                                            : false
                                        }
                                        type="number"
                                        placeholder="Enter Price"
                                        hideError
                                      />
                                    </div>
                                    <div className="d-flex justify-content-between px-3 text-primary">
                                      <label>USD</label>
                                    </div>
                                  </InputFieldContainer>
                                  {submitCount > 0 && (
                                    <ErrorMessage
                                      name={'price'}
                                      render={formErrorMessage}
                                    />
                                  )}
                                </>
                              ),
                            })}
                          </Col>

                          {!isCatalogue && (
                            <Col xs={12} md={6}>
                              {renderInput({
                                label: 'Discounted Price',
                                toolTip: 'Discounted Price',
                                component: (
                                  <>
                                    <InputFieldContainer
                                      disabled={
                                        values[variationKey]?.length
                                          ? true
                                          : false
                                      }
                                    >
                                      <div className="d-flex align-items-start flex-column flex-grow-1">
                                        <FormikInputWithIcon
                                          name={`oldPrice`}
                                          hideError
                                          id={`oldPrice`}
                                          icon={<>$</>}
                                          formatType="DECIMAL"
                                          disabled={
                                            values[variationKey]?.length
                                              ? true
                                              : false
                                          }
                                          type="number"
                                          placeholder="Enter Discounted Price"
                                        />
                                      </div>
                                      <div className="d-flex justify-content-between px-3 text-primary">
                                        <label>USD</label>
                                      </div>
                                    </InputFieldContainer>
                                    {submitCount > 0 && (
                                      <ErrorMessage
                                        name={'oldPrice'}
                                        render={formErrorMessage}
                                      />
                                    )}
                                  </>
                                ),
                              })}
                            </Col>
                          )}

                          {!isCatalogue && (
                            <Col xs={12} md={6}>
                              {renderInput({
                                label: 'Holiday Price',
                                toolTip:
                                  'Please enter the holiday price, which will be activated once you add dates for the manual holiday.',
                                component: (
                                  <>
                                    <InputFieldContainer
                                      disabled={
                                        values[variationKey]?.length
                                          ? true
                                          : false
                                      }
                                    >
                                      <div className="d-flex align-items-start flex-column flex-grow-1">
                                        <FormikInputWithIcon
                                          name={`holidayPrice`}
                                          hideError
                                          id={`holidayPrice`}
                                          icon={<>$</>}
                                          formatType="DECIMAL"
                                          disabled={
                                            values[variationKey]?.length
                                              ? true
                                              : false
                                          }
                                          type="number"
                                          placeholder="Enter Holiday Price"
                                        />
                                      </div>
                                      <div className="d-flex justify-content-between px-3 text-primary">
                                        <label>USD</label>
                                      </div>
                                    </InputFieldContainer>
                                    {submitCount > 0 && (
                                      <ErrorMessage
                                        name={'holidayPrice'}
                                        render={formErrorMessage}
                                      />
                                    )}
                                  </>
                                ),
                              })}
                            </Col>
                          )}
                          <Col xs={12} md={6}>
                            {renderInput({
                              label: 'Weight',
                              toolTip: 'Weight',
                              component: (
                                <InputFieldContainer>
                                  <div className="d-flex align-items-start flex-column flex-grow-1">
                                    <FormikInputWithIcon
                                      name={`weight`}
                                      // isHideError
                                      id={`weight`}
                                      type="number"
                                      placeholder="Enter Weight"
                                    />
                                  </div>
                                  <FormikSelect
                                    className="custom_select"
                                    name="weightType"
                                    id="weightType"
                                    type="number"
                                    placeholder="Select..."
                                    options={weightTypeList}
                                  />
                                </InputFieldContainer>
                              ),
                            })}
                          </Col>
                        </Row>
                      </ShopStyled.BootstrapCard>
                      <ShopStyled.BootstrapCard className="m-4 mt-0">
                        <StyledText
                          as="h5"
                          $fontSize="24px"
                          color="heading"
                          $fontWeight="600"
                        >
                          Variations
                        </StyledText>

                        <Row className="mt-2 mt-md-3">
                          <Col xs={12} className="mb-2">
                            <FieldArray
                              name={variationKey}
                              render={(arrayHelpers) => (
                                <div>
                                  {values?.[variationKey]?.map(
                                    (variationDTO, index) => (
                                      <Row
                                        key={`variation-${variationDTO.title}-${index}`}
                                        name={`variation-${index}`}
                                        className=" rounded shadow px-2 py-4 mb-4 position-relative"
                                      >
                                        {(!values[variationKey]?.[index]
                                          ?.defaultVariation ||
                                          values[variationKey]?.length ===
                                            1) && (
                                          <VariationDeleteIconContainer>
                                            <StyledButton
                                              onClick={() => {
                                                setShowDeletePopUp(true);
                                                setDeleteIndexId(index);
                                                arrayHelperRef.current =
                                                  arrayHelpers;
                                              }}
                                              textButton
                                              type="button"
                                            >
                                              <RoundedCloseIcon size={35} />
                                            </StyledButton>
                                          </VariationDeleteIconContainer>
                                        )}

                                        <Col xs={12} md={6}>
                                          <Row className="d-flex justify-content-end">
                                            <Col
                                              style={{
                                                marginBottom: '-54px',
                                              }}
                                              className="d-flex justify-content-end"
                                            >
                                              {isFlorist && (
                                                <div className="mt-2 d-flex flex-row">
                                                  {renderInput({
                                                    label: 'Stock Status',
                                                    toolTip: 'Stock Status',
                                                    horizontal: true,
                                                    component: (
                                                      <OverlayTrigger
                                                        overlay={
                                                          !values?.stockStatus &&
                                                          values[
                                                            variationKey
                                                          ]?.[index]
                                                            ?.outOfStock ? (
                                                            <StyledTooltip>
                                                              If you want to
                                                              change the product
                                                              as In Stock here,
                                                              you need to mark
                                                              the entire product
                                                              In Stock first.
                                                            </StyledTooltip>
                                                          ) : values[
                                                              variationKey
                                                            ]?.[index]
                                                              ?.defaultVariation ? (
                                                            <StyledTooltip>
                                                              To set this
                                                              variation as out
                                                              of stock you need
                                                              to set other
                                                              variation as
                                                              default
                                                            </StyledTooltip>
                                                          ) : (
                                                            <></>
                                                          )
                                                        }
                                                      >
                                                        <div className="d-flex align-items-center gap-1 mb-2">
                                                          <StyledSwitch
                                                            disabled={
                                                              values[
                                                                variationKey
                                                              ]?.[index]
                                                                ?.defaultVariation ||
                                                              !values?.stockStatus
                                                            }
                                                            enableText="In Stock"
                                                            disableText="Out of Stock"
                                                            width="120px"
                                                            checked={
                                                              !values?.[
                                                                variationKey
                                                              ]?.[index]
                                                                .outOfStock
                                                            }
                                                            onChange={(e) => {
                                                              setFieldValue(
                                                                `${variationKey}.${index}.outOfStock`,
                                                                !e,
                                                              );
                                                            }}
                                                          />
                                                        </div>
                                                      </OverlayTrigger>
                                                    ),
                                                  })}
                                                </div>
                                              )}
                                            </Col>
                                          </Row>

                                          <Row className="mb-3">
                                            <Col xs={12} lg={8}>
                                              {renderInput({
                                                label: 'Title',
                                                toolTip: 'Title',
                                                parentClass: 'mb-0',
                                                component: (
                                                  <div>
                                                    <FormikInputWithIcon
                                                      name={`${variationKey}.${index}.title`}
                                                      id={`${variationKey}.${index}.title`}
                                                      placeholder="Enter title"
                                                      type="text"
                                                      hideError
                                                    />
                                                    {submitCount > 0 && (
                                                      <ErrorMessage
                                                        name={`${variationKey}.${index}.title`}
                                                        render={
                                                          formErrorMessage
                                                        }
                                                      />
                                                    )}
                                                  </div>
                                                ),
                                              })}
                                            </Col>

                                            <Col xs={12} lg={4}>
                                              <div
                                                className="d-flex gap-1 align-items-center h-100"
                                                style={{ marginTop: '10px' }}
                                              >
                                                <ShopStyled.NewFormLabel className="d-flex align-items-center m-0">
                                                  <span className="title">
                                                    Default:
                                                  </span>
                                                </ShopStyled.NewFormLabel>
                                                <ShopStyled.EditButton
                                                  color={'dark70'}
                                                  variant={'icon-btn-text'}
                                                  padding="11px 13px"
                                                  type="button"
                                                  name={`variation-${index}.defaultVariation`}
                                                  className="bg-transparent  border-0"
                                                  childrenProps={{
                                                    className:
                                                      'd-flex align-items-center gap-2',
                                                  }}
                                                  disabled={
                                                    values[variationKey]?.[
                                                      index
                                                    ]?.outOfStock
                                                      ? true
                                                      : false
                                                  }
                                                >
                                                  <StyledCheckbox
                                                    disabled={
                                                      values[variationKey]?.[
                                                        index
                                                      ]?.outOfStock
                                                        ? true
                                                        : false
                                                    }
                                                    name={`variation-${index}.defaultVariationCheckbox`}
                                                    value={
                                                      variationDTO?.defaultVariation
                                                    }
                                                    size={30}
                                                    onChange={(event) => {
                                                      if (
                                                        event.target.checked
                                                      ) {
                                                        if (
                                                          values[variationKey]
                                                            ?.length === 1
                                                        ) {
                                                          setFieldValue(
                                                            `${variationKey}.${index}.defaultVariation`,
                                                            true,
                                                          );
                                                          return;
                                                        }
                                                        values?.[
                                                          variationKey
                                                        ]?.forEach(
                                                          (item, i) => {
                                                            if (i === index) {
                                                              setFieldValue(
                                                                `${variationKey}.${i}.defaultVariation`,
                                                                event.target
                                                                  .checked,
                                                              );
                                                              return;
                                                            }
                                                            setFieldValue(
                                                              `${variationKey}.${i}.defaultVariation`,
                                                              false,
                                                            );
                                                          },
                                                        );
                                                      } else {
                                                        return toast.error(
                                                          'One variation must be selected',
                                                        );
                                                      }
                                                    }}
                                                  />
                                                </ShopStyled.EditButton>
                                              </div>
                                            </Col>
                                          </Row>
                                          <div className="mt-2">
                                            {renderInput({
                                              label: 'Variation Images',
                                              toolTip: 'Variation Images',
                                              component: (
                                                <>
                                                  <FormikDropZone
                                                    name={`${variationKey}.${index}.imageDTOList`}
                                                    id={`${variationKey}.${index}.imageDTOList`}
                                                    size="large"
                                                    onDrop={async (val) => {
                                                      const payload =
                                                        await onDropBulkUploadVariations(
                                                          values[variationKey][
                                                            index
                                                          ].imageDTOList ?? [],
                                                          val,
                                                        );

                                                      setFieldValue(
                                                        `${variationKey}.${index}.imageDTOList`,
                                                        payload,
                                                      );
                                                    }}
                                                  />
                                                  <CarouselTheming.SelectImageButton
                                                    className="mt-2"
                                                    onClick={() => {
                                                      setOpenImageGalleryModal({
                                                        isProductVariation: true,
                                                        variationIndex: index,
                                                      });
                                                    }}
                                                    bgprimary="true"
                                                  >
                                                    <EditIcon /> Select Image
                                                    From Gallery
                                                  </CarouselTheming.SelectImageButton>

                                                  <div className="d-flex gap-2 mt-4 w-100 overflow-auto">
                                                    {values[variationKey][
                                                      index
                                                    ].imageDTOList?.map(
                                                      (img, ind) => (
                                                        <StyledImageContainer
                                                          key={`${ind}-variation-img`}
                                                        >
                                                          <StyledUserImage
                                                            onClick={() => {
                                                              const updatedImages =
                                                                values[
                                                                  variationKey
                                                                ][
                                                                  index
                                                                ].imageDTOList?.map(
                                                                  (img, i) =>
                                                                    i === ind
                                                                      ? {
                                                                          ...img,
                                                                          thumbnail: true,
                                                                        }
                                                                      : {
                                                                          ...img,
                                                                          thumbnail: false,
                                                                        },
                                                                );
                                                              setFieldValue(
                                                                `${variationKey}.${index}.imageDTOList`,
                                                                updatedImages,
                                                              );
                                                            }}
                                                            className={`cursor-pointer flex-center align-items-center justify-content-center ${
                                                              img?.thumbnail
                                                                ? 'active'
                                                                : ''
                                                            }`}
                                                          >
                                                            <img
                                                              loading="lazy"
                                                              src={
                                                                img?.thumbnailUrl ||
                                                                img?.url
                                                              }
                                                              alt={
                                                                img?.fileName
                                                              }
                                                            />
                                                            <div className="check-icon">
                                                              <CheckIcon />
                                                            </div>
                                                          </StyledUserImage>
                                                          <div
                                                            className="delete-icon cursor-pointer"
                                                            onClick={() => {
                                                              let filteredImages =
                                                                values[
                                                                  variationKey
                                                                ][
                                                                  index
                                                                ].imageDTOList?.filter(
                                                                  (_, i) =>
                                                                    i !== ind,
                                                                );

                                                              if (
                                                                filteredImages &&
                                                                filteredImages.length >
                                                                  0
                                                              ) {
                                                                filteredImages =
                                                                  filteredImages?.map(
                                                                    (img, i) =>
                                                                      i === 0
                                                                        ? {
                                                                            ...img,
                                                                            thumbnail: true,
                                                                          }
                                                                        : {
                                                                            ...img,
                                                                            thumbnail: false,
                                                                          },
                                                                  );
                                                              }

                                                              setFieldValue(
                                                                `${variationKey}.${index}.imageDTOList`,
                                                                filteredImages,
                                                              );
                                                            }}
                                                          >
                                                            <DeleteIcon />
                                                          </div>
                                                        </StyledImageContainer>
                                                      ),
                                                    )}
                                                  </div>
                                                  <StyledText
                                                    $fontSize="14px"
                                                    color="bodyMedium"
                                                  >
                                                    PNG/JPG/JPEG are supported.
                                                    Max file size is 2MB
                                                  </StyledText>
                                                </>
                                              ),
                                            })}
                                          </div>
                                        </Col>
                                        <Col xs={12} md={6}>
                                          <Row>
                                            <Col
                                              xs={12}
                                              xl={!isCatalogue ? 6 : 12}
                                            >
                                              {renderInput({
                                                label: !isCatalogue
                                                  ? 'Price'
                                                  : 'Suggested Price',
                                                toolTip: !isCatalogue
                                                  ? 'Price'
                                                  : 'Suggested Price',
                                                component: (
                                                  <StyledVariationInput>
                                                    <InputFieldContainer>
                                                      <div className="d-flex align-items-start flex-column flex-grow-1">
                                                        <FormikInputWithIcon
                                                          name={`${variationKey}.${index}.price`}
                                                          id={`${variationKey}.${index}.price`}
                                                          icon={<>$</>}
                                                          formatType="DECIMAL"
                                                          type="number"
                                                          placeholder="Enter Price"
                                                          hideError
                                                        />
                                                      </div>
                                                      <div className="d-flex justify-content-between px-3 text-primary">
                                                        <label>USD</label>
                                                      </div>
                                                    </InputFieldContainer>
                                                    {submitCount > 0 && (
                                                      <ErrorMessage
                                                        name={`${variationKey}.${index}.price`}
                                                        render={
                                                          formErrorMessage
                                                        }
                                                      />
                                                    )}
                                                  </StyledVariationInput>
                                                ),
                                              })}
                                            </Col>
                                            {!isCatalogue && (
                                              <>
                                                <Col xs={12} xl={6}>
                                                  {renderInput({
                                                    label: 'Discounted Price',
                                                    toolTip: 'Discounted Price',
                                                    parentClass:
                                                      'position-relative',
                                                    component: (
                                                      <>
                                                        <StyledVariationInput>
                                                          <InputFieldContainer>
                                                            <div className="d-flex align-items-start flex-column flex-grow-1">
                                                              <FormikInputWithIcon
                                                                name={`${variationKey}.${index}.discountedPrice`}
                                                                id={`${variationKey}.${index}.discountedPrice`}
                                                                icon={<>$</>}
                                                                formatType="DECIMAL"
                                                                type="number"
                                                                placeholder="Enter Price"
                                                                hideError
                                                              />
                                                            </div>
                                                            <div className="d-flex justify-content-between px-3 text-primary">
                                                              <label>USD</label>
                                                            </div>
                                                          </InputFieldContainer>
                                                        </StyledVariationInput>
                                                        {submitCount > 0 && (
                                                          <div className="position-absolute">
                                                            <ErrorMessage
                                                              name={`${variationKey}.${index}.discountedPrice`}
                                                              render={
                                                                formErrorMessage
                                                              }
                                                            />
                                                          </div>
                                                        )}
                                                      </>
                                                    ),
                                                  })}
                                                </Col>
                                                <Col xs={12} xl={12}>
                                                  {renderInput({
                                                    label: 'Holiday Price',
                                                    toolTip:
                                                      'Please enter the holiday price, which will be activated once you add dates for the manual holiday.',
                                                    parentClass:
                                                      'position-relative',
                                                    component: (
                                                      <>
                                                        <StyledVariationInput>
                                                          <InputFieldContainer>
                                                            <div className="d-flex align-items-start flex-column flex-grow-1">
                                                              <FormikInputWithIcon
                                                                name={`${variationKey}.${index}.holidayPrice`}
                                                                id={`${variationKey}.${index}.holidayPrice`}
                                                                icon={<>$</>}
                                                                formatType="DECIMAL"
                                                                type="number"
                                                                placeholder="Enter Holiday Price"
                                                                hideError
                                                              />
                                                            </div>
                                                            <div className="d-flex justify-content-between px-3 text-primary">
                                                              <label>USD</label>
                                                            </div>
                                                          </InputFieldContainer>
                                                        </StyledVariationInput>
                                                        {submitCount > 0 && (
                                                          <div className="position-absolute">
                                                            <ErrorMessage
                                                              name={`${variationKey}.${index}.holidayPrice`}
                                                              render={
                                                                formErrorMessage
                                                              }
                                                            />
                                                          </div>
                                                        )}
                                                      </>
                                                    ),
                                                  })}
                                                </Col>
                                              </>
                                            )}
                                          </Row>
                                          <div className="mt-2">
                                            {renderInput({
                                              label: 'Recipe (Internal Only)',
                                              toolTip: 'Recipe (Internal Only)',
                                              isRequired: false,
                                              component: (
                                                <div style={{ color: 'black' }}>
                                                  <FormikHTMLInputField
                                                    as="textarea"
                                                    onCharacterCountExceed={
                                                      setIsButtonDisable
                                                    }
                                                    name={`${variationKey}.${index}.recipe`}
                                                    id={`${variationKey}.${index}.recipe`}
                                                    placeholder="Enter Recipe"
                                                  />
                                                </div>
                                              ),
                                            })}
                                          </div>
                                        </Col>
                                      </Row>
                                    ),
                                  )}
                                  <ShopStyled.EditButton
                                    type="button"
                                    name="add Variation"
                                    onClick={() => {
                                      arrayHelpers.push({
                                        title: '',
                                        price: 0,
                                        stockStatus: true,
                                        ...(!isCatalogue
                                          ? { discountedPrice: null }
                                          : {}),
                                        defaultVariation:
                                          !values[variationKey]?.length,
                                        imageDTOList: [],
                                      });

                                      setFieldValue(`stockStatus`, true);
                                    }}
                                    color={'dark70'}
                                    variant={'icon-btn-text'}
                                    padding="9.5px 16px"
                                    className="bg-transparent mb-3 align-self-end"
                                    childrenProps={{
                                      className:
                                        'd-flex align-items-center gap-2',
                                    }}
                                  >
                                    <AddIcon />
                                    Add
                                  </ShopStyled.EditButton>
                                </div>
                              )}
                            />
                          </Col>
                        </Row>
                      </ShopStyled.BootstrapCard>
                      {!isViewOnly && (
                        <ShopStyled.BootstrapCardFooter
                          className={'gap-2 gap-md-3 mt-2 '}
                        >
                          <ShopStyled.EditButton
                            color={'heading'}
                            type="button"
                            variant={'outline'}
                            style={{
                              boxShadow: 'none',
                            }}
                            onClick={() =>
                              isComingFromEditTemplate
                                ? onHandleButtonActionForTemplate()
                                : navigate(ProductsNavRoute)
                            }
                          >
                            <ShopStyled.ButtonText>
                              Cancel
                            </ShopStyled.ButtonText>
                          </ShopStyled.EditButton>
                          <OverlayTrigger
                            overlay={
                              !isCatalogue &&
                              (pid
                                ? !has_permission_to_edit
                                : !has_permission_to_add_product) ? (
                                <StyledTooltip>
                                  {pid
                                    ? 'You do not have permission to edit product'
                                    : 'You do not have permission to add product'}
                                </StyledTooltip>
                              ) : (
                                <></>
                              )
                            }
                          >
                            <div>
                              <ShopStyled.EditButton
                                color={'primary'}
                                type="submit"
                                disabled={
                                  loading ||
                                  isButtonDisable ||
                                  (!isCatalogue &&
                                    (pid
                                      ? !has_permission_to_edit
                                      : !has_permission_to_add_product))
                                }
                                loading={loading}
                              >
                                <ShopStyled.ButtonText>
                                  {pid ? 'Update' : 'Add'} Product
                                </ShopStyled.ButtonText>
                              </ShopStyled.EditButton>
                            </div>
                          </OverlayTrigger>
                        </ShopStyled.BootstrapCardFooter>
                      )}
                    </ShopStyled.BootstrapCard>
                  </Form>
                );
              }}
            </Formik>
          ) : (
            <ShopStyled.BootstrapCard>
              <StyledLoading />
            </ShopStyled.BootstrapCard>
          )}
        </Container>

        {showDeletePopUp && (
          <DeleteConfirmation
            show={showDeletePopUp}
            bodyText={
              'Do you want to delete these record? This process cannot be undone.'
            }
            onDelete={() => {
              setShowDeletePopUp(false);
              handleDeleteClick();
            }}
            onCancel={() => {
              setShowDeletePopUp(false);
            }}
          />
        )}
        <ImageSelectionModal
          openImageGalleryModal={openImageGalleryModal ? true : false}
          setOpenImageGalleryModal={() => setOpenImageGalleryModal(null)}
          setSelectedImage={setSelectedImage}
        />
      </ShopStyled.PageWrap>
    </>
  );
};
const StyledImageContainer = styled.div`
  position: relative;

  .delete-icon {
    background-color: ${({ theme }) => theme.white};
    position: absolute;
    right: -8px;
    padding: 6px;
    border-radius: 50%;
    top: -13px;
    color: ${({ theme }) => theme.error};
  }
`;
const StyledUserImage = styled.div`
  width: 96px;
  height: 96px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.primaryLight2};
  box-shadow: rgba(0, 0, 0, 0.05) 0px 1px 2px 0px;
  position: relative;
  border-radius: 8px;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .check-icon {
    display: none;
    position: absolute;
    color: ${({ theme }) => theme.white};
    padding: 3px 6px;
    z-index: 1;
  }

  &.active {
    .check-icon {
      display: block;
    }

    &:after {
      opacity: 1;
    }
  }

  &:after {
    content: '\\A';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.6);
    opacity: 0;
    transition: all 1s;
    -webkit-transition: all 1s;
  }
`;
const InputFieldContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  .selected__css {
  }
  .async-select {
    [class$='-control'] {
      padding-block: 0 !important;
      border: 0 !important;
    }
  }
  border-radius: 6px;
  background: ${({ theme, disabled }) =>
    disabled ? theme.dark80 : theme.white};
  border: ${({ $borderWidth }) => $borderWidth ?? '1.5px'} solid
    ${({ theme }) => theme.dark70};
  input {
    border: 0;
  }
  ._styled-input {
    width: 100%;
    border-right: ${({ $borderWidth }) => $borderWidth ?? '1.5px'} solid
      ${({ theme }) => theme.dark70};
    > div {
      padding: 9.4px 7px;
      border: 0;
    }
  }
`;

const StyledVariationInput = styled.div`
  [class$='-ValueContainer'] {
    padding: 2px 2px 2px 8px !important;
  }
`;

const AddonBg = styled.div`
  display: flex;
  background: ${({ theme }) => theme.primary}21;
  position: relative;
  cursor: grab;
  width: max-content;
  gap: 10px;
  margin: 10;
  margin-bottom: 10px;
  padding: 8px 14px;
  margin-right: 15px;
  border-radius: 10px;
  align-items: center;
`;

const DeleteIconBg = styled.div`
  position: absolute;
  right: -10px;
  top: -10px;
  height: 24px;
  width: 24px;
  color: red;
  cursor: pointer;
`;

const VariationDeleteIconContainer = styled.div`
  position: absolute;
  top: -15px;
  left: -30px;
`;
export default CreateProduct;
